import React from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';

export default function Phase3ProcurementType({ wizardData, setWizardData, onNext, onPrev, aiLoading, onGetAIRecommendation }) {
  return (
    <div className="space-y-6">
      <div>
        <Label className="text-lg font-semibold mb-4 block">How would you like to determine the procurement type?</Label>
        
        <RadioGroup value={wizardData.typeSelectionMethod} onValueChange={(value) => setWizardData({ ...wizardData, typeSelectionMethod: value, procurementType: '', aiRecommendedType: null })}>
          <div className="space-y-4">
            {/* Option 1: Manual Selection */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-3">
                <RadioGroupItem value="manual" id="manual" />
                <Label htmlFor="manual" className="cursor-pointer font-semibold">I know my procurement type</Label>
              </div>
              
              {wizardData.typeSelectionMethod === 'manual' && (
                <div className="ml-7 mt-4 space-y-3">
                  <Label className="text-sm">Select procurement type:</Label>
                  <RadioGroup value={wizardData.procurementType} onValueChange={(value) => setWizardData({ ...wizardData, procurementType: value })}>
                    <div className="space-y-2">
                      {[
                        { value: 'goods', label: 'Goods' },
                        { value: 'infrastructure', label: 'Infrastructure Projects' },
                        { value: 'consulting', label: 'Consulting Services' },
                        { value: 'mixed', label: 'Mixed (Combination)' }
                      ].map((option) => (
                        <div key={option.value} className="flex items-center space-x-2">
                          <RadioGroupItem value={option.value} id={`type-${option.value}`} />
                          <Label htmlFor={`type-${option.value}`} className="cursor-pointer">{option.label}</Label>
                        </div>
                      ))}
                    </div>
                  </RadioGroup>
                </div>
              )}
            </div>

            {/* Option 2: AI Recommendation */}
            <div className="border rounded-lg p-4 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20">
              <div className="flex items-center space-x-3 mb-3">
                <RadioGroupItem value="ai" id="ai" />
                <Label htmlFor="ai" className="cursor-pointer font-semibold flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-purple-600" />
                  Get AI Recommendation
                </Label>
              </div>
              
              {wizardData.typeSelectionMethod === 'ai' && (
                <div className="ml-7 mt-4 space-y-3">
                  <Label className="text-sm">Describe your project:</Label>
                  <Textarea
                    placeholder="e.g., We need to construct a new school building with 10 classrooms and procure furniture and equipment for the classrooms..."
                    value={wizardData.projectDescription}
                    onChange={(e) => setWizardData({ ...wizardData, projectDescription: e.target.value })}
                    rows={4}
                    className="resize-none"
                  />
                  <Button 
                    onClick={onGetAIRecommendation} 
                    disabled={!wizardData.projectDescription.trim() || aiLoading}
                    className="w-full"
                    variant="outline"
                  >
                    {aiLoading ? 'Analyzing...' : 'Get AI Recommendation'}
                    <Sparkles className="w-4 h-4 ml-2" />
                  </Button>
                  
                  {wizardData.aiRecommendedType && (
                    <div className="mt-4 p-4 bg-white dark:bg-slate-800 rounded border border-emerald-200 dark:border-emerald-800 space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
                          AI Recommendation: <span className="capitalize">{wizardData.aiRecommendedType}</span>
                        </p>
                        <span className="text-xs px-2 py-1 rounded bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300">
                          {wizardData.aiConfidence} confidence
                        </span>
                      </div>
                      {wizardData.aiTopMatches && wizardData.aiTopMatches.length > 0 && (
                        <div className="text-xs text-slate-600 dark:text-slate-400 pt-2 border-t">
                          <p className="font-medium mb-1">Top Match:</p>
                          <p className="italic">{wizardData.aiTopMatches[0].name}</p>
                          {wizardData.aiTopMatches[0].matched_keywords && wizardData.aiTopMatches[0].matched_keywords.length > 0 && (
                            <p className="text-[10px] mt-1 text-slate-500">
                              Keywords: {wizardData.aiTopMatches[0].matched_keywords.join(', ')}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </RadioGroup>
      </div>
      
      <div className="flex gap-3">
        <Button variant="outline" onClick={onPrev} size="lg">
          <ChevronLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <Button onClick={onNext} className="flex-1 bg-emerald-600 hover:bg-emerald-700" size="lg">
          Next <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
